import {IServiceFactory} from "../service-factory.interface";
import {
    IStationConfiguration,
    IStationMarketConfiguration
} from "../configuration/objects/stations-configurations.interface";
import {ICountryConfiguration} from "../configuration/objects/country-configuration.interface";
import {IStation} from "./station.interface";
import {NullableString, NullableUndefinedString} from "../../types/nullable-types";
import {
    ISpecialPriceMarketConfiguration,
    ISpecialPriceMarketSeasonConfiguration
} from "../configuration/objects/special-price-markets-configuration.interface";

export class Mac {
    constructor(public readonly code: string,
                public readonly name: string,
                public readonly macStations: string[],
                public readonly countryCode: string) {
    }
}

export type NullableMac = Mac | null;

export class SpecialPriceMarketSeason {
    constructor(private readonly seasonConfig: ISpecialPriceMarketSeasonConfiguration) {
    }

    get name(): string {
        return this.seasonConfig.name;
    }

    get enabled(): boolean {
        return this.seasonConfig.enabled;
    }

    appliesToBundle(bundle: string): boolean {
        return this.enabled && bundle === this.seasonConfig.bundle;
    }

    get agent(): string {
        return this.seasonConfig.agent;
    }
}

export class SpecialPriceMarket {
    constructor(private readonly configuration: ISpecialPriceMarketConfiguration,
                private readonly services: IServiceFactory) {
        this._seasons = (configuration.seasons ?? []).map(seasonConfig => new SpecialPriceMarketSeason(seasonConfig));
    }

    private readonly _seasons: SpecialPriceMarketSeason[];

    get marketName(): string {
        return this.configuration.marketName;
    }

    getAgent(forActiveSeason: boolean): string {
        if(forActiveSeason && this.activeSeason) {
            return this.activeSeason.agent;
        } else {
            return this.configuration.agent;
        }

    }


    getMarketNameTranslation(): string {
        switch (this.configuration.marketName.toLowerCase()) {
            case 'sardinia':
                return this.services.language.translate('Sardinia');
            case 'marche':
                return this.services.language.translate('Marche');
            default:
                return this.configuration.marketName
        }
    }



    getAgentForBundle(bundle: string): NullableString {
        if(bundle === this.configuration.bundle) {
            return this.configuration.agent;
        }

        return this._seasons.find(s => s.appliesToBundle(bundle))?.agent ?? null;
    }

    get activeSeason(): SpecialPriceMarketSeason | null {
        return this._seasons.find(s => s.enabled) ?? null;
    }
}

export class Station implements IStation {
    constructor(private readonly services: IServiceFactory,
                private readonly stationConfig: IStationConfiguration,
                private readonly mac: NullableMac) {
    }

    get stationCode(): string {
        return this.stationConfig.code;
    }

    get stationMacCode(): string {
        if(this.mac?.code) {
            return this.mac.code;
        }

        return this.stationCode;
    }

    get stationName(): string {
        return this.stationConfig.name;
    }

    get stationMacName(): string {
        if(this.mac?.name) {
            return this.mac.name;
        }

        return this.stationName;
    }

    get marketsConfig(): IStationMarketConfiguration[] {
        return this.stationConfig.markets
    }

    get allMacStations(): string[] {
        return this.mac?.macStations ?? [];
    }

    private get country(): ICountryConfiguration | null {
        const country = this.services.country.tryGetCountry(this.stationConfig.countryCode);
        if(!country) {
            this.services.logger.error(`Missing country code "${this.stationConfig.countryCode}" for station ${this.stationCode}`);
        }
        return country;
    }

    get countryCode(): string {
        return this.country?.code || '';
    }

    get countryName(): string {
        return this.country?.name || '';
    }

    get markets(): Station[] {
        const markets: Station[] = []
        this.marketsConfig.forEach(m => {
            let station = this.services.stations.tryGetStation(m.code);
            if(station) {
                markets.push(station);
                if(station.mac) {
                    station = this.services.stations.tryGetStation(station.mac.code);
                    if(station) {
                        markets.push(station);
                    }
                }
            }
        });
        return markets.distinct(m => m.stationCode, m => m);
    }

    isOnlineCheckInAllowed(destination: Station): boolean {
        const market = this.marketsConfig.find(m => m.code === destination.stationCode);
        if(market) {
            return market.allowedOnlineCheckIn;
        }

        return false;
    }
}

export type NullableStation = Station | null;

export interface IStationService {
    getAllStations(): Station[];
    findStations(stationCodes: string[]): Station[];
    getStation(stationCode: string): Station;
    tryGetStation(stationCode: NullableUndefinedString): NullableStation;
    stationExists(stationCode: string): boolean;
    groupStationsAlphabetically(stations: Station[]): Record<string, Station[]>;
    getSpecialPriceMarket(from: NullableStation, to: NullableStation): SpecialPriceMarket | null;
}


